import { useCallback, useEffect, useRef, useState } from "react";

export type VoiceStatus = "idle" | "recording" | "transcribing";

export type VoiceErrorKind = "permission" | "transcribe";

export interface UseVoiceInputOptions {
  /**
   * Injected transcription function — accepts a recorded `Blob` and returns the
   * transcript as a string. Lives in the `api` layer; injected here so `shared`
   * stays clean of any network dependency.
   */
  transcribe: (blob: Blob, fileName?: string) => Promise<string>;
  /** Receives the trimmed transcript once recording stops + transcribes. */
  onTranscript: (text: string) => void;
  /** Surfaced on a denied mic permission or a failed transcription. */
  onError: (kind: VoiceErrorKind) => void;
}

export interface UseVoiceInput {
  status: VoiceStatus;
  /** Start when idle, stop (→ transcribe) when recording; no-op while transcribing. */
  toggle: () => void;
  /** False when the browser lacks `MediaRecorder` / `getUserMedia` (hide the control). */
  supported: boolean;
}

const SUPPORTED =
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function" &&
  typeof MediaRecorder !== "undefined";

/**
 * Voice input hook: records mic audio with `MediaRecorder`, then calls the
 * injected `transcribe` fn and hands back the text. Tracks an
 * idle → recording → transcribing lifecycle, stops mic tracks on stop / unmount,
 * and reports permission / transcription failures via `onError`.
 *
 * Lives in `shared` because it contains no network or feature-specific logic —
 * the transcription implementation is dependency-injected by the caller.
 */
export function useVoiceInput({
  transcribe,
  onTranscript,
  onError,
}: UseVoiceInputOptions): UseVoiceInput {
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Latest callbacks via refs so an in-flight recording always resolves against
  // the current composer value (and a fresh toast handler).
  const transcribeRef = useRef(transcribe);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    transcribeRef.current = transcribe;
    onTranscriptRef.current = onTranscript;
    onErrorRef.current = onError;
  });

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  // Stop the mic if we unmount mid-recording.
  useEffect(() => releaseStream, [releaseStream]);

  const start = useCallback(async () => {
    if (!SUPPORTED) return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      onErrorRef.current("permission");
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const type = recorder.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      recorderRef.current = null;
      releaseStream();

      if (blob.size === 0) {
        setStatus("idle");
        return;
      }

      setStatus("transcribing");
      transcribeRef
        .current(blob, "recording.webm")
        .then((text) => {
          const trimmed = text.trim();
          if (trimmed) onTranscriptRef.current(trimmed);
        })
        .catch(() => onErrorRef.current("transcribe"))
        .finally(() => setStatus("idle"));
    };

    recorder.start();
    setStatus("recording");
  }, [releaseStream]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }, []);

  const toggle = useCallback(() => {
    if (status === "recording") stop();
    else if (status === "idle") void start();
  }, [status, start, stop]);

  return { status, toggle, supported: SUPPORTED };
}
